import { createApi } from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/react";

import usersService from "./users/services.ts";
import { clientApi } from "./api.ts";
import notificationsService from "@/redux/notifications/services.ts";
import organizationsService from "./organizations/services.js";
import chaptersService from "./chapters/services.ts";
import surveyService from "./survey/services.ts";
import commentsService from "./comments/services.js";
import dataBlocksService from "./datablocks/services.ts";
import filesService from "./files/services.ts";
import surveyViewsService from "./survey-views/services.ts";
import commonService from "./common/services.ts";
import invitesService from "@/redux/invites/services.js";
import membershipsService from "@/redux/memberships/services.js";
import analyticsService from "@/redux/analytics/services.js";
import authenticationService from "@/redux/authentication/services.js";
import discussionsService from "@/redux/discussions/services.js";
import applicationsService from "@/redux/applications/services.ts";

const axiosBaseQuery =
  ({ baseUrl }) =>
  async (props) => {
    try {
      const url = props?.url || props;
      const result = await clientApi({
        url: baseUrl + url,
        method: props?.method || "GET",
        data: props?.data,
        params: props?.params,
        headers: props?.headers,
      });
      return { data: result.data };
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          url: props?.url,
          method: props?.method || "GET",
          params: props?.params,
        },
      });
      return {
        error: {
          status: error.response?.status,
          data: error.response?.data || error.message,
        },
      };
    }
  };

export const commonsApi = createApi({
  reducerPath: "commonsApi",
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_APP_BACKEND_URL,
  }),
  endpoints: (builder) => ({
    ...authenticationService(builder),
    ...discussionsService(builder),
    ...commonService(builder),
    ...usersService(builder),
    ...notificationsService(builder),
    ...organizationsService(builder),
    ...invitesService(builder),
    ...membershipsService(builder),
    ...chaptersService(builder),
    ...surveyService(builder),
    ...surveyViewsService(builder),
    ...commentsService(builder),
    ...dataBlocksService(builder),
    ...filesService(builder),
    ...analyticsService(builder),
    ...applicationsService(builder),
  }),
});

export const {
  useGetCountriesQuery,
  useGetLanguagesQuery,
  useGetCountryByIdQuery,
  useGetLanguageByIdQuery,
  useLazyGetCountriesQuery,
  useLazyGetLanguagesQuery,
  useLoginMutation,
  useRegisterMutation,
  useResetPasswordMutation,
  useResetPasswordConfirmMutation,
  useVerifyEmailMutation,
  useVerifyEmailResendMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadNotificationsCountQuery,
  useVerifyUserInviteEmailQuery,
  useBulkMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useLogOutMutation,
  useDeleteUserByIdMutation,
  useDeleteOrganizationByIdMutation,
  useLazyGetOrganizationByIdQuery,
  usePatchOrganizationByIdMutation,
  useUpdateOrganizationByIdMutation,
  useDeleteMembershipByIdMutation,
  useCreateUserInviteMutation,
  useDeleteUserInviteByIdMutation,
  useLazyGetOrganizationsQuery,
  useCreateOrganizationMutation,
  usePatchUserInviteByIdMutation,
  usePatchMembershipByIdMutation,
  useLazyGetMembershipsQuery,
  useGetChaptersQuery,
  useLazyGetChaptersQuery,
  useLazyGetSurveyStatisticsQuery,
  useGetSectionByIdQuery,
  useGetSurveyStatisticsQuery,
  useGetQuestionByIdQuery,
  useGetQuestionSuggestionsQuery,
  useLazyGetQuestionSuggestionsQuery,
  useGetQuestionSuggestionStatusQuery,
  useLazyGetQuestionSuggestionStatusQuery,
  useCreateAnswerMutation,
  useDeleteAnswerMutation,
  useUpdateAnswerMutation,
  usePatchAnswerMutation,
  useGetSurveyByIdQuery,
  useGetSurveyContributorsQuery,
  useLazyGetSectionsQuery,
  useUpdateSurveyAssignmentMutation,
  useRevertSurveyAssignmentSubmissionMutation,
  useImportOrganizationsFromCsvMutation,
  useLazyGetSurveysQuery,
  useLazyGetAdminSurveyStatisticsQuery,
  useSendSurveyAssignmentsMutation,
  useExportOrganizationDataMutation,
  useLazyGetExportedOrganizationDataQuery,
  useDelistOrganizationMutation,
  useExportUserDataMutation,
  useLazyGetExportedUserDataQuery,
  useLazyGetUserByIdQuery,
  useUpdateUserByIdMutation,
  useLazyGetAnswerByIdQuery,
  useLazyImpersonateUserQuery,
  useLazyPaginateFilterDiscussionsQuery,
  useGetDiscussionsStatisticsQuery,
  useLazyGetUsersQuery,
  useCreateDiscussionMutation,
  useGetSubjectsQuery,
  useLazyGetSubjectsQuery,
  useGetTopicsQuery,
  useLazyGetTopicsQuery,
  useGetDiscussionByIdQuery,
  useLikeDiscussionMutation,
  useGetDiscussionLikersQuery,
  useLazyGetDiscussionLikersQuery,
  useLazyGetDiscussionByIdQuery,
  useUpdateDiscussionMutation,
  useDeleteDiscussionMutation,
  usePatchDiscussionMutation,
  useLazyReportDiscussionQuery,
  useCreateTopicMutation,
  useUpdateTopicMutation,
  useDeleteTopicMutation,
  useLazyGetCommentsQuery,
  useDeleteCommentMutation,
  useCreateCommentMutation,
  useLikeCommentMutation,
  useGetCommentLikersQuery,
  useLazyGetCommentLikersQuery,
  useUpdateCommentMutation,
  useLazyReportCommentQuery,
  usePatchCommentMutation,
  useLazyIncrementDiscussionViewsQuery,
  useLazyGetSurveyViewsQuery,
  useLazyGetSurveyViewsWithSurveysQuery,
  useLazyIncrementSurveyViewViewsQuery,
  useLazyDownloadSurveyViewQuery,
  useLazyViewSurveyViewQuery,
  useGetSurveyViewPreviewMutation,
  useDeleteSurveyViewMutation,
  useLazyGetQuestionsQuery,
  useGetAnswersQuery,
  useLazyGetAnswersQuery,
  useGetBaseAnswersQuery,
  useCreateSurveyViewMutation,
  useGetSurveyViewByIdQuery,
  useUpdateSurveyViewMutation,
  useLazyGetSurveyByIdQuery,
  useGetSurveyAssignmentsQuery,
  useLazyGetSurveyAssignmentsQuery,
  useLazyGetUserInvitesQuery,
  useGetUsersQuery,
  useGetNotificationSettingsForUserQuery,
  usePatchNotificationSettingsMutation,
  useGetEmailSettingsForUserQuery,
  usePatchEmailSettingsMutation,
  useGetCommentQuery,
  useGetTopicNotificationSettingsForUserQuery,
  useCreateTopicNotificationSettingsMutation,
  usePatchTopicNotificationSettingsMutation,
  useGetDataTemplatesQuery,
  useGetDataBlocksQuery,
  useLazyGetDataBlocksQuery,
  useCreateDataBlockMutation,
  useLazyGetDataFilesQuery,
  useCreateDataFileMutation,
  useGetDataFilesQuery,
  useLazyGetPublicDataFileQuery,
  useDeleteDataFileMutation,
  useGetDataBlockByIdQuery,
  useLazyGetDataBlockByIdQuery,
  useLazyGetDataTemplateByIdQuery,
  useGetDataTemplateByIdQuery,
  usePatchDataBlockMutation,
  useDeleteDataBlockMutation,
  useUpdateDataBlockMutation,
  useGetChoiceOptionsQuery,
  useLazyIncrementDataBlockViewsQuery,
  useCreateBulkDataBlockRequestsMutation,
  useLazyGetDataBlockRequestsQuery,
  useDeleteDataBlockRequestMutation,
  useRequestDataBlockEditMutation,
  usePatchDataBlockRequestMutation,
  useGetDataBlockRequestByIdQuery,
  useLazyGetDataBlockRequestByIdQuery,
  useGetProfileTypesQuery,
  useLazyGetProfileTypesQuery,
  useGetUserProfileTypesQuery,
  useCreateUserProfileTypeMutation,
  useUpdateUserProfileTypeMutation,
  usePatchUserProfileTypeMutation,
  useLazyGetAreasOfExpertiseQuery,
  useGetAreasOfExpertiseQuery,
  useGetDataFileExtensionsQuery,
  useGetDataFileReferencesQuery,
  useGetOrganizationAnalyticsQuery,
  useGetUserAnalyticsQuery,
  useGetEvidenceAnalyticsQuery,
  useLazyGetAchievementsQuery,
  useGetAchievementsQuery,
  useLazyGetCommunityRolesQuery,
  useGetCommunityRolesQuery,
  useLazyGetPasswordResetLinkQuery,
  usePatchUserByIdMutation,
  useGetSurveySamplesQuery,
  useGetSurveyChecklistQuery,
  useUpdateSurveyChecklistMutation,
  useGetDataBlockVersionsQuery,
  useGetDataBlockHistoryQuery,
  useGetCookieConsentQuery,
  useUpdateCookieConsentMutation,
  useLazyGetApplicationsQuery,
  useSubmitApplicationMutation,
  useVerifyApplicationMutation,
  useAcceptApplicationByIdMutation,
  useRejectApplicationByIdMutation,
  useLookupOrganizationDomainsMutation,
  useLazyGetOrganizationDomainsQuery,
  useCreateOrganizationDomainMutation,
  useUpdateOrganizationDomainMutation,
  useDeleteOrganizationDomainByIdMutation,
  useVerifyApplicationTokenQuery,
} = commonsApi;
